import styled from '@emotion/styled';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager';
import { advancedFlowViewStore } from '../view-advanced-flow/store/AdvancedFlowViewStore';

import { useI18n } from '../../../hooks/useI18nFormatters';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { GradientButton } from '../../../components/buttons/GradientButton';
import { IconButton } from '../../../components/buttons/IconButton';
import { Glyph } from '../../../components/common/Glyph';
import { SpinnerLoading } from '../../../components/common/SpinnerLoading';
import { Heading2Medium } from '../../../components/common/text/Heading2Medium';
import { TextField } from '../../../components/forms/TextField';
import { Dialog } from '../../../components/overlay/Dialog';

export function SaveAdvancedFlowDialog() {
  const { i18n } = useI18n();
  const formId = 'save-advanced-flow-dialog-form';
  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
    },
  });

  function onSubmit(data) {
    setIsSaving(true);
    const values = { ...data };
    const waitPromise = new Promise((resolve) => setTimeout(resolve, 500));

    advancedFlowViewStore.updateAdvancedFlow((prevAdvancedFlow) => {
      return {
        ...prevAdvancedFlow,
        name: values.name,
      };
    });

    const savePromise = advancedFlowViewStore.saveAdvancedFlow({
      route: true,
    });

    Promise.all([waitPromise, savePromise])
      .then(([, result]) => {
        ToastManager.add({
          icon: glyph.core.checkmark,
          message: i18n.messageFormatter(`flow.createdAdvancedFlow`, {
            name: result.name,
          }),
        });
        setIsSaving(false);
      })
      .catch((error) => {
        setIsSaving(false);
      });
  }

  function handleClose() {
    RouteManager.pushState({});
  }

  return (
    <Dialog onClose={handleClose} isOpen>
      <SaveAdvancedFlowDialog.Root>
        <SaveAdvancedFlowDialog.Header>
          <IconButton
            onPress={handleClose}
            url={glyph.core.cross}
            color={theme.color.icon_light}
            size={theme.icon.size_default}
          />

          <SaveAdvancedFlowDialog.HeaderIconWrapper>
            <Glyph
              source={glyph.system.advancedFlow}
              size={theme.icon.size_large}
              color={theme.color.icon_dark}
            />
          </SaveAdvancedFlowDialog.HeaderIconWrapper>

          <SaveAdvancedFlowDialog.HeaderTitle>
            {i18n.messageFormatter('flow.newFlowNamePlaceholder')}
          </SaveAdvancedFlowDialog.HeaderTitle>
        </SaveAdvancedFlowDialog.Header>

        <SaveAdvancedFlowDialog.Form id={formId} onSubmit={handleSubmit(onSubmit)}>
          <TextField
            autoFocus
            autoComplete="off"
            orientation="vertical"
            type="text"
            name="name"
            label={i18n.messageFormatter('common.name')}
            placeholder={i18n.messageFormatter('flow.newFlowNamePlaceholder')}
            register={register}
            required
            minLength={1}
            error={errors.name}
          />
        </SaveAdvancedFlowDialog.Form>

        <SaveAdvancedFlowDialog.Divider />

        <SaveAdvancedFlowDialog.Footer>
          <GradientButton form={formId} type="submit" flat>
            {/* Note the sizes are linked 16 -> theme.icon.size_small */}
            {isSaving ? (
              <SpinnerLoading size={16} color={theme.color.mono_000} />
            ) : (
              <GradientButton.Icon
                source={glyph.core.checkmark}
                color={theme.color.white}
                size={theme.icon.size_default}
              />
            )}
            <GradientButton.Text>{i18n.messageFormatter('common.save')}</GradientButton.Text>
          </GradientButton>
        </SaveAdvancedFlowDialog.Footer>
      </SaveAdvancedFlowDialog.Root>
    </Dialog>
  );
}

SaveAdvancedFlowDialog.Root = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 400px;
`;

SaveAdvancedFlowDialog.HeaderIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${su(8)};
  height: ${su(8)};
  background-color: ${theme.color.mono_025};
  border-radius: 50%;
`;

SaveAdvancedFlowDialog.HeaderTitle = styled(Heading2Medium)``;

SaveAdvancedFlowDialog.Header = styled.header`
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  justify-content: center;
  padding: ${su(2, 2, 0, 2)};

  ${IconButton.S.Root} {
    align-self: flex-end;
  }

  ${SaveAdvancedFlowDialog.HeaderIconWrapper} {
    align-self: center;
  }

  ${SaveAdvancedFlowDialog.HeaderTitle} {
    align-self: center;
    padding-top: ${su(2)};
  }
`;

SaveAdvancedFlowDialog.Form = styled.form`
  padding: ${su(0, 2, 1, 2)};

  ${TextField.Root} {
    margin: ${su(2)} 0;
  }
`;

SaveAdvancedFlowDialog.Divider = styled.div`
  background-color: ${theme.color.line};
  height: 1px;
  margin: 0 ${su(2)};
`;

SaveAdvancedFlowDialog.Footer = styled.footer`
  padding: ${su(2)};

  ${GradientButton.S.Root} {
    width: 100%;
  }
`;
